/** Wide two-panel "Add property" picker, shared by the Assisted editor and the
 *  reusable SchemaConfigGrid.
 *
 *  Left panel = the options list (full identifiers visible); right panel = help
 *  for the currently highlighted option. Generic by design: the consumer supplies
 *  the option list and a `renderHelp(key)` for the right panel, so this stays
 *  decoupled from either editor's schema/row types. React context carries the
 *  hovered key across the Autocomplete's Popper portal, and the Paper component is
 *  module-level (stable) so the listbox isn't remounted — and its keyboard/scroll
 *  state kept — on every hover change.
 *
 *  Browser + password-manager autofill is suppressed on the input: config keys
 *  like `api_key`/`secret_key` otherwise trigger password managers to offer to
 *  fill the picker (the icon overlapping "Add property…").
 */
import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material';
import { createContext, useContext, useState, type HTMLAttributes, type ReactNode } from 'react';

export interface AddPropertyOption {
  key: string;
  /** Optional type label shown inline after the key (e.g. "string", "array"). */
  type?: string;
}

interface HoverContextValue {
  hoveredKey: string | null;
  renderHelp: (key: string) => ReactNode;
}

const AddPropertyHoverContext = createContext<HoverContextValue>({
  hoveredKey: null,
  renderHelp: () => null,
});

function AddPropertyPaper({ children, ...paperProps }: HTMLAttributes<HTMLElement>) {
  const { hoveredKey, renderHelp } = useContext(AddPropertyHoverContext);
  return (
    <Paper
      {...paperProps}
      sx={{ width: '100%', display: 'flex', overflow: 'hidden', '& .MuiAutocomplete-listbox': { maxHeight: 420 } }}
    >
      <Box sx={{ flex: '0 0 52%', minWidth: 0, borderRight: 1, borderColor: 'divider' }}>{children}</Box>
      <Box sx={{ flex: '1 1 48%', minWidth: 0, maxHeight: 420, overflowY: 'auto' }}>
        {hoveredKey ? (
          renderHelp(hoveredKey)
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Hover or arrow to a property to see its type, description, and default.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

interface Props {
  options: AddPropertyOption[];
  /** Selected key (controlled). */
  value: string | null;
  onChange: (key: string | null) => void;
  /** Render the help panel for the highlighted option key. */
  renderHelp: (key: string) => ReactNode;
  inputWidth?: number;
  popupWidth?: number;
}

export function AddPropertyPicker({
  options,
  value,
  onChange,
  renderHelp,
  inputWidth = 320,
  popupWidth = 760,
}: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const selected = options.find((o) => o.key === value) ?? null;
  return (
    <AddPropertyHoverContext.Provider value={{ hoveredKey, renderHelp }}>
      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(o) => o.key}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        renderOption={(props, o) => (
          <Box component="li" {...props} sx={{ fontSize: 13 }}>
            <Box component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>
              {o.key}
            </Box>
            {o.type && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {o.type}
              </Typography>
            )}
          </Box>
        )}
        onHighlightChange={(_, o) => setHoveredKey(o?.key ?? null)}
        value={selected}
        onChange={(_, o) => onChange(o?.key ?? null)}
        onClose={() => setHoveredKey(null)}
        sx={{ width: inputWidth }}
        slotProps={{ popper: { style: { width: popupWidth } } }}
        PaperComponent={AddPropertyPaper}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Add property…"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
              // Stop browser + password-manager autofill (api_key/secret_key etc.).
              'data-1p-ignore': true,
              'data-lpignore': 'true',
              'data-form-type': 'other',
            }}
          />
        )}
      />
    </AddPropertyHoverContext.Provider>
  );
}
